import React from 'react';
import { useHistory } from 'react-router-dom';
import useFetch from 'use-http';
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/shared/heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SlashIcon, Download } from 'lucide-react';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { useUserState } from '@/components/context/UserContext';

const VaccinationDetails = () => {
  const history = useHistory();
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');
  const vaccination = history.location.state?.data;
  const userState = useUserState();

  const formatDateWithoutConversion = (date) => {
    if (!date) {
      return '-';
    }

    const value = String(date);
    const datePart = value.substring(0, 10);
    const timePart = value.substring(11, 16);

    if (!datePart) {
      return '-';
    }

    if (!timePart || timePart === '00:00') {
      return datePart;
    }

    return `${datePart} ${timePart}`;
  };

  const formatDateOnlyWithoutConversion = (date) => {
    if (!date) {
      return '-';
    }

    return String(date).substring(0, 10);
  };

  const handleFileDownload = async () => {
    await getFile(
      `?fileType=${vaccination.file.type}&fileGuid=${vaccination.file.guid}&category=Vaccination&espbiEsi=${userState.espbiEsi}`,
    ).then((res) => {
      const base64Data = res;
      const blob = base64toBlob(base64Data, 'application/pdf');
      const downloadLink = document.createElement('a');

      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download =
        vaccination.name +
        ' ' +
        formatDateOnlyWithoutConversion(vaccination.occurrenceDateTime) +
        '.pdf';

      document.body.appendChild(downloadLink);

      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 120);
    });
  };

  function base64toBlob(base64Data, contentType = '') {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);

      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }

      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  if (!vaccination) {
    return null;
  }

  return (
    <div className="space-y-5">
      <Breadcrumb className="px-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/vaccinations">Skiepai</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{vaccination.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">
            {vaccination.name}
          </Heading>
        </CardHeader>

        <CardContent className="space-y-2">
          <div>
            <span>Data: </span>
            <span>
              {formatDateWithoutConversion(vaccination.occurrenceDateTime)}
            </span>
          </div>

          {vaccination.practitioner?.name && (
            <div>
              <span>Gydytojas: </span>
              <span>
                {vaccination.practitioner?.qualification} -{' '}
                {vaccination.practitioner?.name}{' '}
                {vaccination.practitioner?.surname}
              </span>
            </div>
          )}

          {vaccination.diagnosis && (
            <div>
              <span>Pagrindinė diagnozė: </span>
              <span>{vaccination.diagnosis}</span>
            </div>
          )}

          {vaccination.targetDisease && (
            <div>
              <span>Liga: </span>
              <span>{vaccination.targetDisease}</span>
            </div>
          )}

          {vaccination.e063StatusDisplay?.toLowerCase() !== 'pasirašytas' && (
            <div className="text-red-600 font-medium">LAUKIAMA REZULTATŲ</div>
          )}

          {vaccination.doseQuantity && (
            <div>
              <span>Dozė: </span>
              <span>{vaccination.doseQuantity}</span>
            </div>
          )}

          {vaccination.lotNumber && (
            <div>
              <span>Serijos numeris: </span>
              <span>{vaccination.lotNumber}</span>
            </div>
          )}

          {vaccination.vaccine && (
            <div>
              <span>Vakcinos pavadinimas: </span>
              <span>{vaccination.vaccine}</span>
            </div>
          )}

          {vaccination.manufacturer && (
            <div>
              <span>Gamintojas: </span>
              <span>{vaccination.manufacturer}</span>
            </div>
          )}

          {vaccination.nextImmunizationDate && (
            <div>
              <span>Kitos vakcinos data: </span>
              <span>
                {formatDateWithoutConversion(vaccination.nextImmunizationDate)}
              </span>
            </div>
          )}
        </CardContent>

        <CardFooter />
      </Card>

      {vaccination.file?.guid && (
        <Card>
          <CardHeader>
            <Heading variant="userAccountDetailsTitle">Dokumentai</Heading>
          </CardHeader>

          <CardContent>
            <Button variant="light" onClick={handleFileDownload}>
              <Download />
              Atsisiųsti PDF
            </Button>
          </CardContent>

          <CardFooter />
        </Card>
      )}
    </div>
  );
};

export default VaccinationDetails;
